import { Box } from '@mui/system';
import React from 'react';
import { useConvertPaidTrafficFieldName } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/PaidSearch/paidSearchUtil';
import KeyMetricCards, {
  KeyMetricCardDefinition
} from 'src/components/BeaconV2Components/KeyMetricCards/KeyMetricCards';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { CommonSummaryInfoSubtitle } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import { KEY_METRICS_CONTAINER_ADJUSTED_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { useAppSelector } from 'src/utils/Hooks';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useCreateRemoveRetailPriceRangeFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { PAID_SEARCH_TREND_CHART_ID, usePaidSearchMetric } from './PaidSearchMetricContext';
import { AdvertisingMetricLabels } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdvertisingConstants';

/**
 * Card title overrides
 */
const titleOverrides = {
  impressions: AdvertisingMetricLabels.AD_IMPRESSION,
  searchKeyword: AdvertisingMetricLabels.KEYWORDS,
  costPerClick: AdvertisingMetricLabels.CPC,
  costPerAcquisition: AdvertisingMetricLabels.CPA,
  conversionRate: AdvertisingMetricLabels.CONVERSION,
  clicks: AdvertisingMetricLabels.AD_CLICK,
  spend: AdvertisingMetricLabels.AD_SPEND,
  sales: AdvertisingMetricLabels.AD_SALES,
  unitsSold: AdvertisingMetricLabels.AD_UNITS_SOLD
};

/**
 * Overrides for getting the field
 */
const fieldOverrides = {
  searchKeyword: 'weekId'
};

/**
 * Aggregation builders for each card by field name
 */
const aggregationBuilders = {
  searchKeyword: (aggregationBuilder: AggregationBuilder) => aggregationBuilder.addConditionRangeFilter('clicks', 1)
};

const indexNameOverrides = {
  searchKeyword: 'advertising'
};

const useCardDefinitionsByColumn = () => {
  const removeRetailPriceRangeFilters = useCreateRemoveRetailPriceRangeFilters();
  const indexName = 'advertising';
  const { convertedBuilderFunc } = useConvertPaidTrafficFieldName();

  return [
    ['clicks', 'impressions', 'clickThroughRate', 'searchKeyword'],
    ['spend', 'costPerClick', 'costPerMilli', 'costPerAcquisition'],
    ['sales', 'conversionRate', 'unitsSold', 'returnOnAdSpend']
  ].map((indexNames) =>
    indexNames.map((fieldName) => {
      const def: KeyMetricCardDefinition = {
        indexName: indexNameOverrides[fieldName] || indexName,
        fieldName,
        title: titleOverrides[fieldName],
        entityType: fieldOverrides[fieldName],
        primaryAggregationBuilder: aggregationBuilders[fieldName],
        secondaryAggregationBuilder: aggregationBuilders[fieldName],
        requestBuilder: (builder) => {
          return convertedBuilderFunc(builder.apply(removeRetailPriceRangeFilters()));
        }
      };

      return def;
    })
  );
};

/**
 * Shows the key metric cards on the traffic paid search page
 */
const PaidSearchKeyMetricCards = () => {
  const cardDefinitionsByColumn = useCardDefinitionsByColumn();
  const { setSelectedMetric, setSelectedTrendMetric } = usePaidSearchMetric();

  const scrollToTrendChart = () => {
    if (typeof window === 'undefined') {
      return;
    }

    // Wait for React/chart re-render to settle before scrolling.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.getElementById(PAID_SEARCH_TREND_CHART_ID)?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      });
    });
  };

  const handleKeywordCardClick = () => {
    setSelectedTrendMetric('searchKeyword');
    scrollToTrendChart();
  };

  const enhancedCardDefinitions = cardDefinitionsByColumn.map((column) =>
    column.map((def) => ({
      ...def,
      onClick:
        def.fieldName === 'searchKeyword'
          ? handleKeywordCardClick
          : () => {
              setSelectedMetric(def.fieldName);
              setSelectedTrendMetric(def.fieldName);
            }
    }))
  );

  return (
    <Flex flexDirection="column">
      <Flex flexDirection="column" gap="lg">
        <CommonSummaryInfoSubtitle title="Paid Search" includeLegend />
        <Box
          sx={{
            width: KEY_METRICS_CONTAINER_ADJUSTED_WIDTH,
            transform: 'translate(-16px)'
          }}
        >
          <KeyMetricCards cardDefinitionsByColumn={enhancedCardDefinitions} />
        </Box>
      </Flex>
    </Flex>
  );
};

export default PaidSearchKeyMetricCards;
